import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  Alert,
  KeyboardAvoidingView,
  ScrollView,
  Platform,
  Image,
} from 'react-native';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';
import { useRouter } from 'expo-router';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { supabase } from '@/lib/supabase';
import { biometricService } from '@/services/biometricService';
import { lastUserService, LastUser } from '@/services/lastUserService';
import { Svg, Path } from 'react-native-svg';
import Avatar from '@/components/Avatar';
import PageHeader from '@/components/PageHeader';
import PrimaryButton from '@/components/PrimaryButton';
import SecondaryButton from '@/components/SecondaryButton';

// Task #60: Typed sentinel error so we can distinguish a setSession timeout
// from a Supabase-reported error or any other thrown exception, without
// fabricating a Supabase-shaped error object or using `as any` casts.
class BiometricSetSessionTimeoutError extends Error {
  constructor() {
    super('Biometric setSession timeout');
    this.name = 'BiometricSetSessionTimeoutError';
  }
}

export default function LoginScreen() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [biometricAvailable, setBiometricAvailable] = useState(false);
  const [biometricIsFaceId, setBiometricIsFaceId] = useState(false);
  const [biometricLoading, setBiometricLoading] = useState(false);
  const [lastUser, setLastUser] = useState<LastUser | null>(null);
  const [showLastUser, setShowLastUser] = useState(false);
  // True while a sign-in attempt (password or biometric) is running. Used to
  // lock down the "switch account" affordance so the account can't be changed
  // mid-login.
  const authInProgress = loading || biometricLoading;
  const { signIn } = useAuth();
  const { t } = useLanguage();
  const router = useRouter();
  const insets = useSafeAreaInsets();

  // When we know who used the app last, greet them with a returning-user card
  // (avatar + name) and only ask for the password. The email field is hidden
  // until they tap "Trocar" to switch to a different account.
  const isReturning = showLastUser && !!lastUser;

  useEffect(() => {
    loadLastUser();
    checkAndTriggerBiometric();
  }, []);

  const checkAndTriggerBiometric = async () => {
    try {
      const [enabled, available] = await Promise.all([
        biometricService.isEnabled(),
        biometricService.isAvailable(),
      ]);
      if (enabled && available) {
        const isFace = await biometricService.hasFaceId();
        setBiometricIsFaceId(isFace);
        setBiometricAvailable(true);
        // Auto-trigger OS prompt on cold start
        await attemptBiometricLogin();
      }
    } catch {
      // biometric not available — no-op
    }
  };

  const attemptBiometricLogin = async () => {
    setBiometricLoading(true);
    try {
      const reason = t('biometric.authReason');
      const authenticated = await biometricService.authenticate(reason);
      if (!authenticated) {
        // User canceled — silently fall back to email/password form
        return;
      }

      const stored = await biometricService.getStoredSession();
      if (!stored) {
        // No stored session — disable and fall back silently
        await biometricService.disable();
        setBiometricAvailable(false);
        return;
      }

      // Task #60: Hard timeout around setSession so the loading state is
      // always cleared. If the network or SDK stalls, the user falls back
      // to the email/password form instead of being stuck behind a spinner.
      // Note: Promise.race does not cancel the underlying setSession call.
      // If it eventually succeeds after the timeout, the AuthContext
      // SIGNED_IN handler still picks up the session normally — there is
      // no duplicate state transition because AuthContext is the single
      // owner of the session/user state.
      const SET_SESSION_TIMEOUT_MS = 15000;
      const setSessionPromise = supabase.auth.setSession({
        access_token: stored.access_token,
        refresh_token: stored.refresh_token,
      });
      const timeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new BiometricSetSessionTimeoutError()), SET_SESSION_TIMEOUT_MS),
      );

      let bioData: Awaited<typeof setSessionPromise>['data'] | null = null;
      try {
        const result = await Promise.race([setSessionPromise, timeoutPromise]);
        const { data, error } = result;

        if (error) {
          // Stored tokens revoked or other Supabase-reported error. Classify:
          // only disable biometrics on clearly-revoked tokens; transient
          // errors leave biometrics enabled so the user can retry.
          console.warn('⚠️ Biometric session restore failed:', error.message);
          const msg = error.message || '';
          const isTokensRevoked =
            msg.includes('Invalid Refresh Token') ||
            msg.includes('Refresh Token Not Found') ||
            msg.includes('Invalid token') ||
            msg.includes('refresh_token_not_found') ||
            msg.includes('invalid_grant') ||
            msg.includes('JWT expired');
          if (isTokensRevoked) {
            await biometricService.disable();
            setBiometricAvailable(false);
          }
        } else {
          bioData = data;
        }
      } catch (raceErr) {
        if (raceErr instanceof BiometricSetSessionTimeoutError) {
          // Timeout: keep biometrics enabled so the user can tap the
          // outlined button to retry without restarting the app.
          console.warn('⚠️ Biometric session restore timed out after', SET_SESSION_TIMEOUT_MS, 'ms');
        } else {
          // Genuine unexpected error: rethrow to outer catch which logs.
          throw raceErr;
        }
      }

      if (bioData?.user?.id) {
        // PRD §4 — `auth_login_succeeded` macro event. clinic_id isn't known
        // at this point (AuthContext loads profile asynchronously after sign-in).
        import('@/utils/clarityEvents')
          .then(({ logAuthLoginSucceeded }) =>
            logAuthLoginSucceeded({ method: 'biometric' })
          )
          .catch(() => {});
      }
      // On success, AuthContext SIGNED_IN handler updates user/session and
      // AuthGuard navigates to /(tabs) as soon as `user` is set (Task #60).
    } catch (err) {
      console.error('❌ Biometric login error:', err);
    } finally {
      setBiometricLoading(false);
    }
  };

  const loadLastUser = async () => {
    try {
      const stored = await lastUserService.get();
      if (stored) {
        setLastUser(stored);
        setShowLastUser(true);
      }
    } catch {
      // no stored last user — show the generic email/password form
    }
  };

  const handleSwitchUser = async () => {
    // Switch to the generic login state (show the email field again) and
    // forget the remembered account so it does not reappear on next launch.
    setShowLastUser(false);
    setPassword('');
    setLastUser(null);
    await lastUserService.clear();
    // The user is rejecting the remembered account, so clear any biometric
    // credentials bound to it and hide the biometric button — otherwise a
    // shared device could biometric-restore the previous user's session.
    try {
      await biometricService.disable();
    } catch {
      // ignore
    }
    setBiometricAvailable(false);
  };

  const handleBiometricLogin = async () => {
    await attemptBiometricLogin();
  };

  const handleAuth = async () => {
    // In the returning-user state the email field is hidden, so use the
    // remembered email instead of the (empty) email input.
    const effectiveEmail = isReturning && lastUser ? lastUser.email : email;

    if (!effectiveEmail || !password) {
      Alert.alert(t('common.error'), t('auth.login.fillAllFields'));
      return;
    }

    setLoading(true);

    try {
      console.log('🔐 AuthContext: Attempting sign in...', {
        email: effectiveEmail.toLowerCase().trim(),
        hasPassword: !!password,
        passwordLength: password?.length
      });

      const { data, error } = await supabase.auth.signInWithPassword({
        email: effectiveEmail.toLowerCase().trim(),
        password: password,
      });

      if (data?.user) {
        console.log('✅ Login successful for user:', {
          id: data.user.id,
          email: data.user.email,
          confirmed: data.user.email_confirmed_at ? 'Yes' : 'No'
        });
        // PRD §4 — `auth_login_succeeded` macro event. clinic_id isn't known
        // at this point (AuthContext loads profile asynchronously after sign-in).
        import('@/utils/clarityEvents')
          .then(({ logAuthLoginSucceeded }) =>
            logAuthLoginSucceeded({ method: 'email' })
          )
          .catch(() => {});
      }

      if (error) {
        console.error('Login error:', error);

        // PRD §5.1 — `auth_login_failed`. Map the Supabase error message
        // down to the bounded 3-value `error_code` enum the typed helper
        // accepts (`invalid_credentials | network | other`). Anything that
        // isn't a bad-credentials or transport failure (e.g. unconfirmed
        // email, rate-limited, server config) buckets to `other` so the
        // funnel taxonomy stays small. The verbose Supabase copy is still
        // surfaced to the user via the Alert below — only the metric is
        // bounded.
        const msg = error.message || '';
        let errorCode: 'invalid_credentials' | 'network' | 'other' = 'other';
        if (msg.includes('Invalid login credentials')) errorCode = 'invalid_credentials';
        else if (msg.includes('timeout') || msg.includes('rede') || msg.includes('conexão')) errorCode = 'network';
        import('@/utils/clarityEvents')
          .then(({ logAuthLoginFailed }) => logAuthLoginFailed({ error_code: errorCode }))
          .catch(() => {});

        // Handle specific error types with better connection error detection
        if (error.message.includes('Invalid login credentials')) {
          Alert.alert(t('common.error'), t('auth.login.invalidCredentials'));
        } else if (error.message.includes('Email not confirmed')) {
          Alert.alert(t('common.error'), t('auth.login.emailNotConfirmed'));
        } else if (error.message.includes('Too many requests')) {
          Alert.alert(t('common.error'), t('auth.login.tooManyAttempts'));
        } else if (error.message.includes('timeout') || error.message.includes('rede') || error.message.includes('conexão')) {
          Alert.alert(
            t('common.error'),
            t('auth.login.connectionError'),
            [
              { text: t('auth.login.tryAgain'), onPress: handleAuth },
              { text: t('common.cancel'), style: 'cancel' }
            ]
          );
        } else if (error.message.includes('configuração')) {
          Alert.alert(
            t('common.error'),
            t('auth.login.temporaryError'),
            [
              { text: t('auth.login.tryAgain'), onPress: handleAuth },
              { text: t('common.cancel'), style: 'cancel' }
            ]
          );
        } else {
          Alert.alert(t('common.error'), error.message);
        }
      } else {
        // Successfully signed in - AuthContext will handle state updates
        console.log('Login successful');
      }
    } catch (error) {
      console.error('Unexpected login error:', error);
      // PRD §5.1 — `auth_login_failed` for the unexpected catch (network /
      // SDK / etc). Anything outside the mapped Supabase messages is
      // bucketed as `unknown`.
      import('@/utils/clarityEvents')
        .then(({ logAuthLoginFailed }) => logAuthLoginFailed({ error_code: 'other' }))
        .catch(() => {});
      Alert.alert(
        t('common.error'),
        t('auth.login.unexpectedError'),
        [
          { text: t('auth.login.tryAgain'), onPress: handleAuth },
          { text: t('common.cancel'), style: 'cancel' }
        ]
      );
    } finally {
      setLoading(false);
    }
  };

  const handleBackPress = () => {
    router.back();
  };

  const handleForgotPassword = () => {
    router.push('/forgot-password');
  };

  return (
    <SafeAreaView style={styles.container} edges={['top']}>
      <PageHeader title={t('auth.login.title')} onBack={handleBackPress} />

      <KeyboardAvoidingView
        style={styles.keyboardAvoidingView}
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        keyboardVerticalOffset={Platform.OS === 'ios' ? 0 : 20}
      >
        <ScrollView
          style={styles.scrollView}
          contentContainerStyle={[styles.scrollViewContent, { paddingBottom: insets.bottom + 24 }]}
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
        >
          {/* Content */}
          <View style={styles.section}>
            <Image
              source={require('../assets/images/black-elo-avatar.png')}
              style={styles.symbol}
              resizeMode="contain"
            />

            <Text style={styles.welcomeText}>
              {isReturning ? t('auth.login.welcomeBackReturning') : t('auth.login.welcomeBack')}
            </Text>

            {isReturning && lastUser ? (
              <View style={styles.userCard}>
                <Avatar
                  uri={lastUser.avatarUrl}
                  size={40}
                  borderColor="#C4C2BA"
                  borderWidth={1}
                  useCache={false}
                />
                <View style={styles.userCardInfo}>
                  <Text style={styles.userCardName} numberOfLines={1}>
                    {lastUser.name || lastUser.email}
                  </Text>
                  <Text style={styles.userCardRole}>{t('auth.login.member')}</Text>
                </View>
                <TouchableOpacity
                  style={[styles.switchButton, authInProgress && styles.switchButtonDisabled]}
                  onPress={authInProgress ? undefined : handleSwitchUser}
                  disabled={authInProgress}
                >
                  <Text style={styles.switchButtonText}>{t('auth.login.switchUser')}</Text>
                </TouchableOpacity>
              </View>
            ) : (
              <View style={styles.field}>
                <Text style={styles.label}>{t('auth.login.email')}</Text>
                <TextInput
                  style={styles.input}
                  placeholder={t('auth.login.emailPlaceholder')}
                  placeholderTextColor="#A19F97"
                  color="#151515"
                  value={email}
                  onChangeText={setEmail}
                  keyboardType="email-address"
                  autoCapitalize="none"
                  autoCorrect={false}
                />
              </View>
            )}

            <View style={styles.field}>
              <Text style={styles.label}>{t('auth.login.password')}</Text>
              <View style={styles.passwordFieldWrapper}>
                <TextInput
                  style={styles.passwordInput}
                  placeholder={t('auth.login.passwordPlaceholder')}
                  placeholderTextColor="#A19F97"
                  color="#151515"
                  value={password}
                  onChangeText={setPassword}
                  secureTextEntry={!showPassword}
                  autoCapitalize="none"
                  autoCorrect={false}
                />
                <TouchableOpacity
                  style={styles.eyeButton}
                  onPress={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <Svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <Path
                        d="M11.998 19C15.703 19 19.092 16.3746 21 12C19.092 7.62537 15.703 5 11.998 5C8.297 5 4.908 7.62537 3 12C4.908 16.3766 8.297 19 12.002 19H11.998Z"
                        stroke="#A19F97"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <Path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M15.0788 12.0052C15.0788 13.6785 13.7008 15.0366 12.0028 15.0366C10.3038 15.0366 8.92578 13.6785 8.92578 12.0052C8.92578 10.3308 10.3038 8.97278 12.0028 8.97278C13.7008 8.97278 15.0788 10.3308 15.0788 12.0052Z"
                        stroke="#A19F97"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </Svg>
                  ) : (
                    <Svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <Path
                        d="M7.04401 17.2042C5.36701 15.8162 4.06202 14.0312 3.24902 12.0102C4.07402 9.98024 5.389 8.18825 7.077 6.79225C8.537 5.59325 10.362 4.92925 12.25 4.90625C14.148 4.93125 15.979 5.60325 17.443 6.81125"
                        stroke="#A19F97"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <Path
                        d="M19.9272 4.33594L4.57715 19.6859"
                        stroke="#A19F97"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <Path
                        d="M10.0709 14.1787C9.49191 13.6057 9.16792 12.8247 9.17092 12.0097C9.16792 10.3117 10.5419 8.9327 12.2399 8.9297C13.0579 8.9277 13.8419 9.25169 14.4199 9.82969"
                        stroke="#A19F97"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <Path
                        d="M19.5001 8.94531C20.2021 9.89331 20.7901 10.9203 21.2511 12.0063C19.3371 16.4403 15.9551 19.1103 12.2501 19.1103C11.4131 19.1103 10.5821 18.9743 9.78809 18.7103"
                        stroke="#A19F97"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <Path
                        d="M15.2727 12.5547C15.0437 13.8117 14.0607 14.7977 12.8037 15.0287"
                        stroke="#A19F97"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </Svg>
                  )}
                </TouchableOpacity>
              </View>
            </View>

            {/*
              Task #60: Single loading affordance on the login screen.
              The primary button shows the spinner during BOTH the email/password
              flow and the biometric flow, and the outlined biometric pill button
              is hidden while a biometric login is in progress. This avoids the
              previous double-spinner UX where the bottom outlined button could
              spin indefinitely while AuthGuard waited on profile data.
            */}
            <PrimaryButton
              title={t('auth.login.button')}
              onPress={handleAuth}
              loading={loading || biometricLoading}
            />

            {biometricAvailable && !biometricLoading && (
              <SecondaryButton
                title={biometricIsFaceId ? t('biometric.loginButton') : t('biometric.loginButtonFingerprint')}
                onPress={handleBiometricLogin}
                style={styles.biometricButton}
              />
            )}

            <TouchableOpacity onPress={handleForgotPassword}>
              <Text style={styles.forgotPassword}>{t('auth.login.forgot')}</Text>
            </TouchableOpacity>
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#FFFFFF',
  },

  keyboardAvoidingView: {
    flex: 1,
  },
  scrollView: {
    flex: 1,
  },
  scrollViewContent: {
    flexGrow: 1,
    justifyContent: 'center',
  },

  section: {
    paddingHorizontal: 24,
    paddingVertical: 24,
    gap: 24,
  },
  symbol: {
    width: 38,
    height: 38,
    alignSelf: 'center',
    tintColor: '#C4C2BA',
  },
  welcomeText: {
    color: '#151515',
    fontFamily: 'Sentient-Regular',
    fontSize: 24,
    lineHeight: 29,
    textAlign: 'center',
  },
  userCard: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
    padding: 24,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: '#F0EEE4',
    backgroundColor: '#F9F8F3',
  },
  userCardInfo: {
    flex: 1,
    gap: 4,
  },
  userCardName: {
    color: '#151515',
    fontFamily: 'Sentient-Regular',
    fontSize: 16,
    lineHeight: 22,
  },
  userCardRole: {
    color: '#669AB7',
    fontFamily: 'Inter',
    fontWeight: '500',
    fontSize: 12,
    lineHeight: 12,
  },
  switchButton: {
    height: 40,
    minHeight: 40,
    paddingHorizontal: 12,
    borderRadius: 12,
    backgroundColor: '#F0EEE4',
    justifyContent: 'center',
    alignItems: 'center',
  },
  switchButtonDisabled: {
    opacity: 0.4,
  },
  switchButtonText: {
    color: '#151515',
    fontFamily: 'Inter',
    fontWeight: '500',
    fontSize: 14,
    lineHeight: 20,
  },
  field: {
    gap: 12,
  },
  label: {
    color: '#151515',
    fontFamily: 'Inter',
    fontWeight: '700',
    fontSize: 14,
    lineHeight: 14,
  },
  passwordFieldWrapper: {
    width: '100%',
    position: 'relative',
  },
  input: {
    width: '100%',
    height: 56,
    paddingVertical: 16,
    paddingHorizontal: 12,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#DAD8CF',
    backgroundColor: '#FFFFFF',
    fontFamily: 'Inter_500Medium',
    fontSize: 14,
    lineHeight: 20,
    color: '#151515',
  },
  passwordInput: {
    width: '100%',
    height: 56,
    paddingVertical: 16,
    paddingHorizontal: 12,
    paddingRight: 48,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#DAD8CF',
    backgroundColor: '#FFFFFF',
    fontFamily: 'Inter_500Medium',
    fontSize: 14,
    lineHeight: 20,
    color: '#151515',
  },
  eyeButton: {
    position: 'absolute',
    right: 12,
    top: 0,
    bottom: 0,
    width: 24,
    justifyContent: 'center',
    alignItems: 'center',
  },
  biometricButton: {
    borderColor: '#DAD8CF',
    borderRadius: 12,
  },
  forgotPassword: {
    color: '#015480',
    fontFamily: 'Inter',
    fontWeight: '400',
    fontSize: 14,
    lineHeight: 20,
    textDecorationLine: 'underline',
    textAlign: 'center',
    alignSelf: 'center',
  },
});